import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const PersonalityAssessment = ({ onComplete, isSubmitting }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      id: "Q1",
      question: "How do you usually respond to challenges?",
      options: [
        { value: "A", text: "I avoid them and hope they pass." },
        { value: "B", text: "I push through but feel drained." },
        { value: "C", text: "I look for lessons and try to grow." },
        { value: "D", text: "I thrive under pressure and see challenges as opportunities." }
      ]
    },
    {
      id: "Q2",
      question: "Which statement best describes your daily mindset?",
      options: [
        { value: "A", text: "I often doubt myself." },
        { value: "B", text: "I get things done, but I feel stuck inside." },
        { value: "C", text: "I try to stay positive and focused on growth." },
        { value: "D", text: "I believe in myself and inspire others too." }
      ]
    },
    {
      id: "Q3",
      question: "When it comes to relationships, you tend to…",
      options: [
        { value: "A", text: "Put others first, even if it hurts you." },
        { value: "B", text: "Struggle to open up fully." },
        { value: "C", text: "Balance giving and receiving fairly well." },
        { value: "D", text: "Lead with confidence and boundaries." }
      ]
    },
    {
      id: "Q4",
      question: "What drives you the most?",
      options: [
        { value: "A", text: "Security and stability." },
        { value: "B", text: "Recognition and validation." },
        { value: "C", text: "Growth and learning." },
        { value: "D", text: "Impact and leadership." }
      ]
    },
    {
      id: "Q5",
      question: "Which thought shows up most in your inner voice?",
      options: [
        { value: "A", text: "\"I'm not enough.\"" },
        { value: "B", text: "\"What if I fail?\"" },
        { value: "C", text: "\"I don't know where to start.\"" },
        { value: "D", text: "\"I can't trust myself fully.\"" }
      ]
    },
    {
      id: "Q6",
      question: "What's your biggest struggle right now?",
      options: [
        { value: "A", text: "Confidence in myself." },
        { value: "B", text: "Clarity on my goals." },
        { value: "C", text: "Staying consistent." },
        { value: "D", text: "Feeling fulfilled in relationships." }
      ]
    },
    {
      id: "Q7",
      question: "When you think about your future, you feel…",
      options: [
        { value: "A", text: "Unsure, lost, or anxious." },
        { value: "B", text: "Motivated, but unclear on direction." },
        { value: "C", text: "Excited, but inconsistent." },
        { value: "D", text: "Confident and ready to take action." }
      ]
    },
    {
      id: "Q8",
      question: "If you could change one thing in your life first, what would it be?",
      options: [
        { value: "A", text: "Love and confidence in myself." },
        { value: "B", text: "Clarity and purpose in my path." },
        { value: "C", text: "Consistency in my habits." },
        { value: "D", text: "Deep, fulfilling relationships." }
      ]
    },
    {
      id: "Q9",
      question: "What word feels closest to your ideal self?",
      options: [
        { value: "A", text: "Secure 💖" },
        { value: "B", text: "Empowered ✨" },
        { value: "C", text: "Growing 🌱" },
        { value: "D", text: "Radiant 🌟" }
      ]
    },
    {
      id: "Q10",
      question: "What excites you most about joining Constant Love?",
      options: [
        { value: "A", text: "Daily affirmations to feel better about myself." },
        { value: "B", text: "A community where I belong." },
        { value: "C", text: "AI guidance that helps me grow." },
        { value: "D", text: "A full roadmap to transformation." }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    // Validate all questions answered
    const allAnswered = questions.every(q => answers[q.id]);
    if (!allAnswered) {
      alert("Please answer all questions before submitting");
      return;
    }
    onComplete(answers);
  };

  const currentQ = questions[currentQuestion];
  const currentAnswer = answers[currentQ.id];
  const answeredCount = Object.keys(answers).length;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canProceed = currentAnswer !== undefined;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-20">
      <Card className="w-full max-w-3xl p-6 md:p-10 bg-white/90 backdrop-blur-sm border-2 border-rose-200 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Personality & Character Assessment
          </h1>
          <p className="text-gray-600">
            Answer honestly — there are no wrong answers 💖
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <span className="text-sm font-semibold text-rose-600">
              {answeredCount}/{questions.length} answered
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-rose-400 to-rose-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="mb-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-6 leading-tight break-words">
            {currentQ.question}
          </h2>

          {/* Options */}
          <div className="space-y-4">
            {currentQ.options.map((option) => {
              const isSelected = currentAnswer === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQ.id, option.value)}
                  className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-rose-500 bg-rose-50 shadow-md scale-[1.02]'
                      : 'border-gray-200 bg-white hover:border-rose-300 hover:bg-rose-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 mr-3 sm:mr-4 mt-0.5 flex items-center justify-center ${
                      isSelected
                        ? 'border-rose-500 bg-rose-500'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <CheckCircle className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm sm:text-base md:text-lg leading-snug break-words ${
                        isSelected ? 'font-semibold text-gray-900' : 'text-gray-700'
                      }`}>
                        {option.text}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10">
          <Button
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            variant="outline"
            className={`border-rose-300 text-rose-600 hover:bg-rose-50 ${
              currentQuestion === 0 ? 'invisible' : ''
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed || isSubmitting}
              className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white px-8 py-6 text-lg font-semibold shadow-lg"
            >
              {isSubmitting ? 'Generating Your Blueprint...' : 'Complete Assessment ✨'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="bg-rose-500 hover:bg-rose-600 text-white px-6"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>

        {/* Hint */}
        {!canProceed && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Select an answer to continue
          </p>
        )}
      </Card>
    </div>
  );
};

export default PersonalityAssessment;